
import React from "react";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { ContractBasicDetails } from "./ContractBasicDetails";
import { ContractFinancialDetails } from "./ContractFinancialDetails";
import { ContractReminders } from "./ContractReminders";
import { ContractAttachments } from "./ContractAttachments";
import { ContractFormActions } from "./ContractFormActions";
import { ContractContactsSection } from "./sections/ContractContactsSection";
import { ContractServicesSection } from "./sections/ContractServicesSection";
import { ContractFormModals } from "./ContractFormModals";
import { useContractForm } from "@/hooks/useContractForm";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";
import { FeatureGuard } from "@/components/common/FeatureGuard";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import { useState, useEffect } from "react";

interface ContractFormProps {
  contract?: any;
  mode: "create" | "edit";
}

export function ContractForm({ contract, mode }: ContractFormProps) {
  const { t, tElement } = useEnhancedTranslation();
  const { getPublicBooleanSetting } = useSystemSettings();
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState<boolean>(true);
  
  useEffect(() => {
    const checkSubscriptionsEnabled = async () => {
      try {
        const enabled = await getPublicBooleanSetting('subscriptions_enabled', true);
        setSubscriptionsEnabled(enabled);
      } catch (error) {
        console.error("Error checking if subscriptions are enabled:", error);
        // Default to true if there's an error
        setSubscriptionsEnabled(true);
      }
    };
    
    checkSubscriptionsEnabled();
  }, [getPublicBooleanSetting]);
  
  const {
    form,
    selectedContractType,
    setSelectedContractType,
    selectedContacts,
    setSelectedContacts,
    attachments,
    setAttachments,
    existingAttachments,
    setExistingAttachments,
    contractTypeModalOpen,
    setContractTypeModalOpen,
    categoryModalOpen,
    setCategoryModalOpen,
    contactModalOpen,
    setContactModalOpen,
    currencyModalOpen,
    setCurrencyModalOpen,
    renewalDate,
    servicesTotal,
    handleContractTypeCreated,
    handleCategoryCreated,
    handleContactCreated,
    handleCurrencyCreated,
    handleCompanySelectWithFormUpdate,
    handleServicesTotalUpdate,
    handleServicesApplyCallback,
    onSubmit,
    handleCancelWithConfirmation,
    isPending,
    showUnsavedChangesDialog,
    setShowUnsavedChangesDialog,
    confirmNavigation,
    cancelNavigationDialog,
    translateWrapper
  } = useContractForm(contract, mode);

  const contractTypeId = form.watch("contractTypeId");
  const currencyId = form.watch("currencyId");

  const translateCombined = (key: string, fallback?: string): string => {
    return t(key, fallback || key) || translateWrapper(key, fallback || key) || fallback || key;
  };

  const showServicesSection = contractTypeId && currencyId;
  const showServicesSectionMessage = !showServicesSection && mode === "create";

  return (
    <>
      <ContractFormModals 
        contractTypeModalOpen={contractTypeModalOpen}
        setContractTypeModalOpen={setContractTypeModalOpen}
        categoryModalOpen={categoryModalOpen}
        setCategoryModalOpen={setCategoryModalOpen}
        contactModalOpen={contactModalOpen}
        setContactModalOpen={setContactModalOpen}
        currencyModalOpen={currencyModalOpen}
        setCurrencyModalOpen={setCurrencyModalOpen}
        showUnsavedChangesDialog={showUnsavedChangesDialog}
        setShowUnsavedChangesDialog={setShowUnsavedChangesDialog}
        onConfirmNavigation={confirmNavigation}
        onCancelNavigation={cancelNavigationDialog}
        onContractTypeCreated={handleContractTypeCreated}
        onCategoryCreated={handleCategoryCreated}
        onContactCreated={handleContactCreated}
        onCurrencyCreated={handleCurrencyCreated}
      />
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid gap-6 md:grid-cols-2">
            <ContractBasicDetails 
              form={form}
              selectedContractType={selectedContractType}
              setSelectedContractType={setSelectedContractType}
              setContractTypeModalOpen={setContractTypeModalOpen}
              setCategoryModalOpen={setCategoryModalOpen}
              handleCompanySelect={handleCompanySelectWithFormUpdate}
              handleContractTypeCreated={handleContractTypeCreated}
              handleCategoryCreated={handleCategoryCreated}
            />
            
            <ContractFinancialDetails 
              form={form}
              selectedContractType={selectedContractType}
              renewalDate={renewalDate}
              setCurrencyModalOpen={setCurrencyModalOpen}
              handleCurrencyCreated={handleCurrencyCreated}
              servicesTotal={servicesTotal}
            />
          </div>

          <Separator />
          
          {/* Use FeatureGuard without showPreview to completely hide the feature when subscriptions are disabled */}
          <FeatureGuard feature="contract_services">
            {showServicesSection ? (
              <ContractServicesSection
                form={form}
                contractId={contract?.id}
                contractTypeId={contractTypeId}
                currencyId={currencyId}
                onTotalCostChange={handleServicesTotalUpdate}
                isTemporary={mode === "create"}
                onApplyCallback={handleServicesApplyCallback}
              />
            ) : showServicesSectionMessage && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium">{translateCombined("services", "Services")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {translateCombined("addServicesToContract", "Add services to this contract")}
                  </p>
                </div>
                <Alert variant="default" className="border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription>
                    {translateCombined("selectContractTypeAndCurrency", "Please select a contract type and currency to add services")}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </FeatureGuard>

          {(showServicesSection || showServicesSectionMessage) && <Separator className="my-6" />}
          
          <FeatureGuard feature="reminders">
            <ContractReminders form={form} />
            <Separator />
          </FeatureGuard>

          <ContractContactsSection 
            selectedContacts={selectedContacts}
            setSelectedContacts={setSelectedContacts}
            setContactModalOpen={setContactModalOpen}
            existingContacts={contract?.contactPersons || []}
          />

          <Separator />

          <ContractAttachments 
            existingAttachments={existingAttachments}
            setExistingAttachments={setExistingAttachments}
            attachments={attachments}
            setAttachments={setAttachments}
          />

          <ContractFormActions 
            isPending={isPending}
            mode={mode}
            onCancel={handleCancelWithConfirmation}
            t={translateCombined}
          />
        </form>
      </Form>
    </>
  );
}
